import { useContext, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  BodyWrapper,
  Confirmation,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import { GlobalContext } from '@contexts';
import { FormError } from '@Forms/fields.jsx';
import { ButtonSpinnerIcon, ChevronRightIcon } from '@svgs';
import {
  groupsQuery,
  userDeleteMutation,
  userMutation,
  userQuery,
} from '@queries';

import AccountSidebar from '../components/AccountSidebar/AccountSidebar.jsx';
import UserForm from '../components/UserForm/UserForm.jsx';

export const User = () => {
  const { t, i18n } = useTranslation();
  const { userID } = useParams();
  const navigate = useNavigate();
  const globals = useContext(GlobalContext);
  const queryClient = useQueryClient();

  // get user
  const {
    data: userData,
    isPending: userIsPending,
    isError,
  } = useQuery(
    userQuery(userID, {
      staleTime: Infinity,
    }),
  );

  // save user
  const mutation = useMutation(
    userMutation(userID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['users', 'detail', userID],
        });
      },
    }),
  );

  // delete datamodel
  const deleteMutation = useMutation(
    userDeleteMutation(userID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/users`);
      },
    }),
  );

  // get groups
  const { data: groupsData, isPending: groupsIsPending } =
    useQuery(groupsQuery());

  // list of access group IDs, these are not shown in the UI
  const accessGroupIds = globals.whitelabel.oneoff_access_group_ids
    ? Object.values(globals.whitelabel.oneoff_access_group_ids)
    : [];

  const modifyData = (data) => {
    // update user values before passing to the form
    //
    // update each field.groups to be a single user group ID. Example: 1234
    const defaultData = {
      ...data,
      group_id: data.groups.find((g) => !accessGroupIds.includes(g.group.id))
        .group.id,
    };
    return defaultData;
  };

  // disable user confirmation
  const [showConfirmation, setShowConfirmation] = useState({
    display: false,
    target: null,
    remove: {},
  });
  const toggleModal = (target, remove) => {
    setShowConfirmation({ display: true, target: target, remove: remove });
  };

  return (
    <>
      <Header />
      <BodyWrapper>
        <AccountSidebar />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              <li className="sb-flex">
                <span>Account settings</span>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link to={`/dashboard/users`}>Users</Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                {userIsPending && <span>...</span>}
                {userData && (
                  <span>
                    {userData.first_name} {userData.last_name}
                  </span>
                )}
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            {(userIsPending || groupsIsPending) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {userData && groupsData && (
              <>
                <h1>
                  {userData.first_name} {userData.last_name}
                </h1>
                <UserForm
                  userData={modifyData(userData)}
                  mutation={mutation}
                  groupsData={groupsData}
                />
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Disable"
                    actionVerb="Disabling"
                    label="Disable user"
                    note="This user will be disabled and unable to log in, but can be re-activated by searching"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
